import { useRef, useState } from 'react';
import { ZoomIn, CheckCircle2, Loader2, Minus, Plus, Truck } from 'lucide-react';
import { Input } from '@components/ui/input';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@components/ui/dialog';
import { orderMugs } from '@generated/server/mugs';
import { money, PAYMENT_METHODS } from '@generated/components/pricing';
import { PayPanel } from '@generated/components/PayPanel';

const MUG_PHOTOS = [
  { src: 'https://files-public.monday.com/use1/55159351-241a-4dab-9faf-1232a35d0f96/mugs.JPG', alt: 'Two white Red Hook Star-Revue mugs with the skyline logo' },
  { src: 'https://files-public.monday.com/use1/4bf4e035-0f64-4b49-9f38-909e30e9540c/drinking%20cup.JPG', alt: 'A reader drinking coffee from a Red Hook Star-Revue mug' },
];
const price = (q: number) => Math.floor(q / 2) * 25 + (q % 2) * 15;

export function MugOrder() {
  const [qty, setQty] = useState(1);
  const [photo, setPhoto] = useState(0);
  const [f, setF] = useState({ name: '', email: '', phone: '', address: '', notes: '', paymentMethod: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; amount: number } | null>(null);
  const orderRef = useRef<HTMLElement | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const amount = price(qty);
  const ok = f.name.trim() && f.email.trim() && f.address.trim().length >= 5 && f.paymentMethod;

  const submit = async () => {
    setSaving(true); setError(null);
    try { const r = await orderMugs({ data: { ...f, qty } }); if (r) setDone(r); }
    catch (e) { console.error(e); setError('Your order could not be sent. Please try again, or call 917-652-9128.'); }
    finally { setSaving(false); }
  };

  if (done) return (
    <section className="mx-auto max-w-2xl text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
      <h2 className="mt-3 font-[family-name:var(--font-heading)] text-3xl font-semibold">Thanks — your mug{qty > 1 ? 's are' : ' is'} on the way!</h2>
      <p className="mt-2 text-sm text-muted-foreground">We'll ship as soon as payment arrives. Free shipping.</p>
      <div className="text-left"><PayPanel method={f.paymentMethod} orderId={`MUG-${done.id.slice(-6)}`} dueNow={done.amount} total={done.amount} monthly={false} email={f.email} kind="Mug order" who={f.name || undefined} /></div>
    </section>
  );

  const field = (k: 'name' | 'email' | 'phone', label: string) => (
    <div className="space-y-1.5"><label htmlFor={`m-${k}`} className="text-sm font-medium">{label}</label>
      <Input id={`m-${k}`} type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'} value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-11 rounded-none bg-background" /></div>
  );

  return (
    <section className="mt-14 border-t-4 border-double border-foreground pt-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Sold directly by the Star-Revue</div>
      <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold text-[hsl(var(--brand-blue))]">The Star-Revue Mug</h2>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0 space-y-5">
          <div className="grid gap-5 sm:grid-cols-[220px_1fr]">
            <div>
              <Dialog>
                <DialogTrigger asChild><button type="button" aria-label="Enlarge photo" className="group relative block w-full cursor-zoom-in">
                  <img src={(MUG_PHOTOS[photo] ?? MUG_PHOTOS[0])?.src} alt={(MUG_PHOTOS[photo] ?? MUG_PHOTOS[0])?.alt} className="aspect-square w-full border-2 border-foreground object-cover" />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-foreground/80 px-2 py-1 text-xs font-semibold text-background"><ZoomIn className="h-3.5 w-3.5" />Tap to enlarge</span>
                </button></DialogTrigger>
                <DialogContent className="max-w-[min(95vw,900px)] rounded-none p-2 sm:max-w-[min(95vw,900px)]">
                  <DialogTitle className="sr-only">Star-Revue mug photo</DialogTitle>
                  <img src={(MUG_PHOTOS[photo] ?? MUG_PHOTOS[0])?.src} alt={(MUG_PHOTOS[photo] ?? MUG_PHOTOS[0])?.alt} className="max-h-[85vh] w-full object-contain" />
                </DialogContent>
              </Dialog>
              <div className="mt-2 flex gap-2">{MUG_PHOTOS.map((p, i) => (
                <button key={p.src} type="button" aria-label={`Show photo ${i + 1}`} onClick={() => setPhoto(i)} className={`h-14 w-14 overflow-hidden border-2 ${photo === i ? 'border-primary' : 'border-border'}`}><img src={p.src} alt="" className="h-full w-full object-cover" /></button>
              ))}</div>
            </div>
            <div>
              <div className="flex flex-wrap gap-2">
                <span className="border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-3 py-1 text-lg font-bold">$15 each</span>
                <span className="border-2 border-foreground bg-primary px-3 py-1 text-lg font-bold text-primary-foreground">2 for $25</span>
              </div>
              <p className="mt-3 flex items-center gap-2 text-sm font-semibold"><Truck className="h-4 w-4" />Free shipping by US Mail</p>
              <div className="mt-5 text-sm font-medium">How many?</div>
              <div className="mt-2 flex items-center gap-2">
                <button type="button" aria-label="One fewer" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-11 w-11 items-center justify-center border-2 border-foreground"><Minus className="h-4 w-4" /></button>
                <span className="w-10 text-center text-2xl font-bold tabular-nums">{qty}</span>
                <button type="button" aria-label="One more" onClick={() => setQty((q) => Math.min(20, q + 1))} className="flex h-11 w-11 items-center justify-center border-2 border-foreground"><Plus className="h-4 w-4" /></button>
              </div>
              {qty % 2 === 1 && <p className="mt-2 text-xs text-muted-foreground">Add one more and save — 2 for $25.</p>}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">{field('name', 'Your name')}{field('email', 'Email')}{field('phone', 'Phone (optional)')}</div>
          <div className="space-y-1.5"><label htmlFor="m-addr" className="text-sm font-medium">Delivery address</label>
            <textarea id="m-addr" rows={3} placeholder={'Street and apartment\nCity, State ZIP'} value={f.address} onChange={(e) => set('address', e.target.value)} className="w-full border border-input bg-background p-3 text-sm" /></div>
          <div className="space-y-1.5"><label htmlFor="m-notes" className="text-sm font-medium">Notes (optional)</label>
            <textarea id="m-notes" rows={2} placeholder="A gift? Tell us who it's for." value={f.notes} onChange={(e) => set('notes', e.target.value)} className="w-full border border-input bg-background p-3 text-sm" /></div>
          <div><div className="mb-2 text-sm font-medium">Payment method</div>
            <div className="flex flex-wrap gap-2">{PAYMENT_METHODS.map((m) => (
              <button key={m} type="button" onClick={() => { set('paymentMethod', m); setTimeout(() => orderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); }} className={`min-h-11 border px-3 text-sm ${f.paymentMethod === m ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'}`}>{m}</button>
            ))}</div>
          </div>
        </div>
        <aside ref={orderRef} className={`h-fit border-2 border-foreground bg-card p-5 transition-all ${f.paymentMethod ? 'lg:self-end ring-4 ring-primary/30' : 'lg:sticky lg:top-4'}`}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">Your order</div>
          <div className="mt-2 text-sm">{qty} Star-Revue mug{qty > 1 ? 's' : ''} · free shipping</div>
          <div className="mt-3 font-[family-name:var(--font-heading)] text-4xl tabular-nums">{money(amount)}</div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
          <button type="button" disabled={!ok || saving} onClick={() => void submit()} className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}Order &amp; pay {money(amount)}
          </button>
          {!ok && <p className="mt-2 text-xs text-muted-foreground">Name, email, delivery address and payment method are required.</p>}
        </aside>
      </div>
    </section>
  );
}
